// Client for the seller newsletter API (docs/ROUTE-API-SELLER.md -> "Seller newsletters").
// The server is the authority for consent, quota, and sanitizing; this wrapper only carries
// its structured refusals (`reason`, `data: {quota, recipientCount}`) to the UI intact.

async function readResponse(response, fallback) {
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        const error = new Error(payload.error || fallback);
        error.status = response.status;
        error.reason = payload.meta?.reason || null;
        error.data = payload.data || null;
        throw error;
    }
    return {data: payload.data, meta: payload.meta || {}};
}

export function createNewslettersApi({fetch, apiUrl = ''}) {
    const root = `${apiUrl}/v1/seller/newsletters`;
    const json = {'Content-Type': 'application/json'};

    return {
        async audience(scheduledAt) {
            const query = scheduledAt ? `?scheduledAt=${encodeURIComponent(scheduledAt)}` : '';
            return readResponse(await fetch(`${root}/audience${query}`), 'Unable to load your audience.');
        },
        async credits() {
            return readResponse(await fetch(`${root}/credits`), 'Unable to load extra sends.');
        },
        async creditHistory(beforeId) {
            const query = beforeId ? `?beforeId=${encodeURIComponent(beforeId)}` : '';
            return readResponse(await fetch(`${root}/credits/history${query}`), 'Unable to load credit history.');
        },
        async packs() {
            return readResponse(await fetch(`${root}/packs`), 'Unable to load send packs.');
        },
        async purchases(beforeId) {
            const query = beforeId ? `?beforeId=${encodeURIComponent(beforeId)}` : '';
            return readResponse(await fetch(`${root}/purchases${query}`), 'Unable to load purchases.');
        },
        async purchase(uuid) {
            return readResponse(await fetch(`${root}/purchases/${encodeURIComponent(uuid)}`), 'Unable to refresh payment.');
        },
        async beginPurchase(payload, key) {
            return readResponse(await fetch(`${root}/purchases`, {
                method: 'POST', headers: {...json, 'Idempotency-Key': key},
                body: JSON.stringify(payload),
            }), 'Unable to start payment.');
        },
        async completePurchase(uuid, opaqueData) {
            return readResponse(await fetch(`${root}/purchases/${encodeURIComponent(uuid)}/complete`, {
                method: 'POST', headers: json,
                body: JSON.stringify(opaqueData ? {opaqueData} : {}),
            }), 'Unable to confirm payment.');
        },
        async list() {
            return readResponse(await fetch(root), 'Unable to load your newsletters.');
        },
        async setSignupFormEnabled(signupFormEnabled) {
            return readResponse(await fetch(`${root}/settings`, {
                method: 'PUT',
                headers: json,
                body: JSON.stringify({signupFormEnabled}),
            }), 'Unable to update the signup form.');
        },
        // One Idempotency-Key per composed send. The caller keeps it for retries, so a double
        // click or a dropped connection replays the original send instead of sending twice.
        async send({subject, body, locale, scheduledAt}, idempotencyKey) {
            return readResponse(await fetch(root, {
                method: 'POST',
                headers: {...json, 'Idempotency-Key': idempotencyKey},
                body: JSON.stringify({
                    subject,
                    body,
                    ...(locale ? {locale} : {}),
                    ...(scheduledAt ? {scheduledAt} : {}),
                }),
            }), 'Unable to send this newsletter.');
        },
        // Cancels a scheduled send before it starts. A 404 means it is already gone, which the
        // caller treats as done.
        async cancel(blastId) {
            return readResponse(await fetch(`${root}/${encodeURIComponent(blastId)}`, {
                method: 'DELETE',
            }), 'Unable to cancel this newsletter.');
        },
    };
}

export default createNewslettersApi;
